import { isAnyOf } from '@reduxjs/toolkit';
import type { AppStartListening } from '@/app/listeners';
import { baseApi, endServerSession, refreshSession } from '@/shared/api';
import {
  clearPersistedSession,
  persistSession,
  selectSession,
  sessionExpired,
  sessionRestored,
  sessionStarted,
  signedOut,
} from '@/shared/session';

/** Renew the access token this long before it expires, so requests never meet an expired one. */
const RENEW_AHEAD_MS = 60_000;
/** When the API cannot be reached, try renewing again after this long. */
const RETRY_MS = 30_000;
/** setTimeout cannot wait longer than ~24.8 days. */
const MAX_TIMER_MS = 2_147_483_647;

/**
 * Session lifecycle side effects. Kept in listeners so components and reducers stay pure:
 * - persist the session on sign-in and renewal;
 * - clear storage and all cached server data on sign-out/expiry (sign-out also drops the refresh cookie);
 * - renew the access token silently before it runs out; the session ends only when renewal is refused.
 */
export function registerAuthListeners(startListening: AppStartListening): void {
  startListening({
    actionCreator: sessionStarted,
    effect: ({ payload }) => {
      persistSession(payload.session, payload.persistence);
    },
  });

  startListening({
    actionCreator: signedOut,
    effect: () => {
      endServerSession();
    },
  });

  startListening({
    matcher: isAnyOf(signedOut, sessionExpired),
    effect: (_action, listenerApi) => {
      clearPersistedSession();
      // Never show one user's cached data to the next user.
      listenerApi.dispatch(baseApi.util.resetApiState());
    },
  });

  startListening({
    matcher: isAnyOf(sessionStarted, sessionRestored),
    effect: async (_action, listenerApi) => {
      // Only one expiry timer at a time — a new session replaces the previous timer.
      listenerApi.cancelActiveListeners();
      const session = selectSession(listenerApi.getState());
      if (!session) return;

      let renewAt = Date.parse(session.expiresAt) - RENEW_AHEAD_MS;
      for (;;) {
        const remaining = renewAt - Date.now();
        if (remaining > 0) {
          const sessionChanged = await listenerApi.condition(
            (_, state) => selectSession(state) !== session,
            Math.min(remaining, MAX_TIMER_MS),
          );
          if (sessionChanged) return;
          continue;
        }
        // A renewed session restarts this listener; an ended one stops it.
        const outcome = await listenerApi.pause(refreshSession(listenerApi));
        if (outcome !== 'unavailable') return;
        renewAt = Date.now() + RETRY_MS;
      }
    },
  });
}
